/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BUTTON_FORMAT_SOFT, BUTTON_THEME_DEFAULT, BUTTON_THEME_PRIMARY, Button, ErrorMessage } from '@squiz/sds';
import { COMPONENT_SERVICE_STRINGS } from 'apps/component-service/src/Constants/Strings.constants';
import { getComponentSetPath } from 'apps/component-service/src/Utilities/GetComponentSetPath/GetComponentSetPath';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { getComponentService } from '../../../../../../Services/component';
import { REMOVE_FROM_COMPONENT_SET_DATA_IDS } from '../../RemoveComponentFromSet.constants';

import { RemoveComponentFromSetDialogProps } from './RemoveComponentFromSetDialogProps';

/**
 * RemoveComponentFromSetDialog.
 */
export const RemoveComponentFromSetDialog = (props: RemoveComponentFromSetDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setButtonKeys, setButtons, close, setWebpath, componentKey } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [submit, setSubmit] = useState<boolean>(false);

  const navigateToComponentSet = useCallback(
    (path: string): void => {
      navigate({ pathname: path });
    },
    [navigate],
  );

  // Component service
  const componentService = getComponentService();

  // Component set details path handling
  const currentScreenPath = getCapabilityPath(`/component-sets`);

  const labels = useMemo(() => {
    return {
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      deleteError: t(COMPONENT_SERVICE_STRINGS.deleteError),
      remove: t(COMPONENT_SERVICE_STRINGS.remove),
      removeComponentFromSetDescription: t(COMPONENT_SERVICE_STRINGS.removeComponentFromSetDescription),
    };
  }, [t]);

  const deleteHandler = useCallback(() => {
    setLoading(true);
    setSubmit(false);
    lastValueFrom(componentService.getComponentSetDetails({ webpath: setWebpath }))
      .then((details) => {
        const componentSetDetails = details;
        if (!componentSetDetails) {
          setError(true);
          setLoading(false);
          return;
        }
        const componentSetDto = {
          components: {},
          componentVersionRules: componentSetDetails.componentVersionRules,
          description: componentSetDetails.description,
          displayName: componentSetDetails.displayName,
          environmentVariables: componentSetDetails.environmentVariables,
          headers: componentSetDetails.headers,
        };
        // Remove componentSetDto.componentVersionRules[componentKey] from dto
        delete componentSetDto.componentVersionRules[componentKey];
        // send patch
        /* eslint-disable promise/no-nesting */
        return lastValueFrom(
          componentService.patchComponentSetDetails({ webpath: setWebpath as string, ...componentSetDto }),
        )
          .then(() => {
            navigateToComponentSet(`${currentScreenPath}${getComponentSetPath(`/`, setWebpath)}`);
            return close();
          })
          .catch((err) => {
            setLoading(false);
            setError(true);
          });
      })
      .catch((error) => {
        setSubmit(false);
        setError(true);
        return setLoading(false);
      });
  }, [close, componentKey, componentService, currentScreenPath, navigateToComponentSet, setWebpath]);

  useEffect(() => {
    // Cancel button
    const cancelButtonKey = `cancel`;
    const cancelButton = (
      <Button
        data-id={REMOVE_FROM_COMPONENT_SET_DATA_IDS.cancelButton}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    // Delete button
    const deleteButtonKey = `delete`;
    const deleteButton = (
      <Button
        data-id={REMOVE_FROM_COMPONENT_SET_DATA_IDS.removeButton}
        isLoading={isLoading}
        onClick={(): void => setSubmit(true)}
        text={labels.remove}
        theme={BUTTON_THEME_PRIMARY}
      />
    );
    if (submit && !error) {
      setButtonKeys([cancelButtonKey, deleteButtonKey]);
      setButtons([cancelButton, deleteButton]);
      deleteHandler();
    } else {
      setButtonKeys([cancelButtonKey, deleteButtonKey]);
      setButtons([cancelButton, deleteButton]);
    }
  }, [close, deleteHandler, error, isLoading, labels, setButtonKeys, setButtons, setLoading, submit]);

  return (
    <div>
      <span>{labels.removeComponentFromSetDescription}</span>
      {error && (
        <div className="sds-form-error" data-id={REMOVE_FROM_COMPONENT_SET_DATA_IDS.modalError}>
          <ErrorMessage message={labels.deleteError} />
        </div>
      )}
    </div>
  );
};
